import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';

import { http } from '@/shared/lib/api';
import { useAuth } from '@/shared/auth/AuthContext';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Button } from '@/shared/components/ui/button';

const DISMISS_KEY = 'billing_banner_dismissed';

interface BillingInfo {
  plan: 'standard' | 'business' | 'enterprise';
  operator_count: number;
  operator_limit: number;
  billing_due_date: string | null;
  days_remaining: number | null;
  is_overdue: boolean;
}

interface BillingResponse {
  success: true;
  data: BillingInfo;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

export function BillingBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const isEligible = user?.role === 'operator' || user?.role === 'mobile';

  const query = useQuery<BillingResponse>({
    queryKey: ['settings', 'billing'],
    queryFn: () => http.get<BillingResponse>('/api/settings/billing'),
    enabled: isEligible,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (!isEligible) return null;
  if (dismissed) return null;
  const billing = query.data?.data;
  if (!billing || !billing.billing_due_date) return null;

  const { days_remaining, is_overdue, billing_due_date } = billing;

  // Only warn in the last 3 days before the subscription is due — a due date
  // far in the future isn't "expiring", it just hasn't rolled forward yet
  // after the last payment (see extendSubscriptionForTenant in vana-dash).
  if (!is_overdue && (days_remaining === null || days_remaining > 3)) return null;

  const handleDismiss = (): void => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const variant = is_overdue ? 'destructive' : 'warning';
  const overdueDays = is_overdue ? Math.abs(days_remaining ?? 0) : 0;
  const message = is_overdue
    ? t('billing.banner.overdue', { days: overdueDays })
    : t('billing.banner.expiringIn', {
        days: days_remaining ?? 0,
        date: formatDate(billing_due_date),
      });

  return (
    <Alert variant={variant} className="rounded-none border-x-0 border-t-0">
      <AlertTriangle className="size-4" />
      <AlertDescription className="flex items-center justify-between gap-4">
        <span>{message}</span>
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          onClick={handleDismiss}
          aria-label={t('billing.banner.dismiss')}
          data-testid="billing-banner-dismiss"
        >
          <X className="size-4" />
        </Button>
      </AlertDescription>
    </Alert>
  );
}
